import React, { useState } from 'react';
import { Field, FormRenderProps } from '@progress/kendo-react-form';
import { DataType } from '../../../config/Constants';
import { FieldBox, FieldDataBox } from '../../layout/boxes/FieldBox';
import { Row, Col } from 'react-bootstrap';
import { IRequestAccessPageProps } from '../../services/GraphQLTypes';
import { RequestAccessPageResources as Resources } from '../../../config/Resources';
import { IFormPageProps } from '../../layout/panels/ViewPanel';
import { EntityType } from '../../services/GraphQLModel';
import { FormPanel } from '../../layout/panels/FormPanel';
import { RootState } from '../../../store/reducer/rootReducer';
import { useSelector } from 'react-redux';
import { GET_MAM_SELF_REQUESTABLE_GROUPS_BY_COUNTRY } from '../../services/GraphQLMatrixAccessManager';
import { FormCheckboxGroup, ICheckboxGroupItem } from '../../layout/panels/FormComponents';
import { checkboxGroupValidator, freeTextValidator } from '../../helpers/Validators';

const teamNameOptions: ICheckboxGroupItem[] = [
  { value: 'Investment', text: 'Investment' },
  { value: 'Activation', text: 'Activation' },
  { value: 'Client', text: 'Client' },
  { value: 'GCS', text: 'GCS' },
  { value: 'GOC', text: 'GOC' },
  { value: 'WPP Media Solutions', text: 'WPP Media Solutions' },
  { value: 'Other', text: 'Other (please specify)', textField: 'teamNameOther' },
];
const teamNameValidator = checkboxGroupValidator(teamNameOptions);

const roleScopeOptions: ICheckboxGroupItem[] = [
  { value: 'Local Market', text: 'Local Market (please specify)', textField: 'roleScopeLocalMarket' },
  { value: 'Regional', text: 'Regional (please specify the region or markets)', textField: 'roleScopeRegional' },
  { value: 'Other', text: 'Other (please specify)', textField: 'roleScopeOther' },
  { value: 'Global', text: 'Global' },
];
const roleScopeValidator = checkboxGroupValidator(roleScopeOptions);

export function RequestAccessPageFormPage(props: IFormPageProps<IRequestAccessPageProps>): React.ReactElement {
  const selectSecurity = (state: RootState) => state.security;
  const currentSecurity = useSelector(selectSecurity);
  const loggedInEmail = currentSecurity?.userInfo?.userPrincipalName ?? "";
  const parent = (props.initialValues as any)?.parent;
  const initialValues = {
    ...props.initialValues,
    requestor: loggedInEmail,
    appInstance: parent?.appInstance ?? '',
    teamName: [],
    teamNameOther: '',
    clientTeamName: '',
    roleScope: [],
    roleScopeLocalMarket: '',
    roleScopeRegional: '',
    roleScopeOther: '',
    approversContactDetails: ''
  };
  const [groupHint, setGroupHint] = useState('');
  const handleGroupChange = (event: any) => {
    const selectedValue = event.target?.value || event.value;
    if (selectedValue && selectedValue.description) {
      setGroupHint(selectedValue.description);
    } else {
      setGroupHint('');
    }
  };

  const formContent = (formRenderProps: FormRenderProps): React.ReactElement => {
    const setFormValue = (name: string, value: string) => formRenderProps.onChange(name, { value });
    return (
      <>
        <Row>
          <Col>
            <FieldBox
              field={'requestor'}
              title={Resources.Requestor}
              disabled={true}  
            />
          </Col>          
        </Row>
        <Row>
          <Col md={4}>
            <FieldBox
              field={'appInstance'}
              title={Resources.ApplicationInstance}
              required={true}
              disabled={true}
            />
          </Col>
          <Col md={8}>
            <FieldDataBox
              field={'group'}
              title={Resources.Group}
              placeholder={Resources.Group_Placeholder}
              type={DataType.ComboBox}
              required={true}
              disabled={!formRenderProps.valueGetter('appInstance')}
              variables={{
                country: formRenderProps.valueGetter('appInstance')
              }}
              dataSource={GET_MAM_SELF_REQUESTABLE_GROUPS_BY_COUNTRY}
              mutateData={(data: any[]) =>
                data.map(g => ({
                  id: g.groupId,
                  display: g.groupName,
                  description: g.description
                }))
              }
              hint={groupHint ? `Description: ${groupHint}` : ''}
              onChange={handleGroupChange}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Field
              id={'teamName'}
              field={'teamName'}
              name={'teamName'}
              title={Resources.TeamName_Title}
              component={FormCheckboxGroup}
              data={teamNameOptions}
              validator={teamNameValidator}
              required={true}
              getText={formRenderProps.valueGetter}
              setText={setFormValue}
            />
          </Col>
        </Row>
        <Row>
          <Col>
              <FieldBox
                field={'clientTeamName'}
                title={Resources.ClientTeamName_Title}
                placeholder={Resources.ClientTeamName_Placeholder}
                validator={freeTextValidator}
                required={true}
              />
          </Col>
        </Row>
        <Row>
          <Col>
            <Field
              id={'roleScope'}
              field={'roleScope'}
              name={'roleScope'}
              title={Resources.RoleScope_Title}
              component={FormCheckboxGroup}
              data={roleScopeOptions}
              // Wider right column so the long Regional label stays on one line.
              columns={'2fr 3fr'}
              validator={roleScopeValidator}
              required={true}
              getText={formRenderProps.valueGetter}
              setText={setFormValue}
            />
          </Col>
        </Row>
        <Row>
          <Col>
              <FieldBox
                field={'approversContactDetails'}
                title={Resources.ApproversContactDetails_Title}
                placeholder={Resources.ApproversContactDetails_Placeholder}
                validator={freeTextValidator}
                required={true}
              />
          </Col>
        </Row>
      </>
    );
  };
  return <FormPanel {...props} type={EntityType.AccessRequest} getElements={formContent} initialValues={initialValues} className="request-access-form"/>;
}